import {
  ERichMenuAreaActionType,
  ERichMenuImageContentType,
  ERichMenuTemplateCategory,
} from './enum'
export type TRichMenuSize = {
  width: number
  height: number
}

export type TRichMenuBounds = {
  x: number
  y: number
  width: number
  height: number
}

export type TRichMenuTemplate = {
  id: string
  label: string
  category: ERichMenuTemplateCategory
  size: TRichMenuSize
  areaKeys: string[]
  bounds: TRichMenuBounds[]
}

export type TRichMenuTemplatesResponse = {
  templates: TRichMenuTemplate[]
}

export const RICH_MENU_IMAGE_ACCEPT = [
  ERichMenuImageContentType.Jpeg,
  ERichMenuImageContentType.Jpg,
  ERichMenuImageContentType.Png,
  '.jpg',
  '.jpeg',
  '.png',
].join(',')

/*
 * LINE Rich Menu API
 * @see https://developers.line.biz/en/reference/messaging-api/#rich-menu-object
 */
export const LINE_RICH_MENU_IMAGE_MAX_BYTES = 1024 * 1024
export const LINE_RICH_MENU_IMAGE_MIN_WIDTH = 800
export const LINE_RICH_MENU_IMAGE_MAX_WIDTH = 2500
export const LINE_RICH_MENU_IMAGE_MIN_HEIGHT = 250
export const LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_NUMERATOR = 145
export const LINE_RICH_MENU_IMAGE_MIN_ASPECT_RATIO_DENOMINATOR = 100
export const RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH = 50
export const RICH_MENU_URI_MAX_LENGTH = 1000
export const RICH_MENU_ACTION_LABEL_MAX_LENGTH = 20

/**
 * BAN誘導ラップURL（/mem/richmenu/click）の固定部に見込む文字数。実測の内訳は
 *
 * | `/mem/richmenu/click`            |  19 |
 * | `?to=`                           |   4 |
 * | `&channelId=` ＋ Int最大10桁     |  21 |
 * | `&openExternalBrowser=1`         |  22 |
 * | `&sig=` ＋ 署名16桁              |  21 |
 * | 固定部小計                       |  87 |
 * | 公開ベースURLの枠                | 128 |
 *
 * 公開ベースURLの実長はフロントからは分からないため枠で見込む。ここは入力を早く
 * 弾くための保守的な門で、正となるのはバックエンドの適用直前チェック。
 * バックエンドの `CLICK_WRAP_OVERHEAD` と同じ値を保つこと。
 */
const RICH_MENU_CLICK_WRAP_OVERHEAD = 215

/**
 * mailto: を encodeURIComponent した後に許容する最大長。
 * LINEへ登録するのはBAN誘導ラップURLに包んだ後のURLで、
 * その全体が LINE の URI 上限(1000文字)を受けるため、ラップ分を差し引く。
 */
export const RICH_MENU_MAILTO_ENCODED_MAX_LENGTH =
  RICH_MENU_URI_MAX_LENGTH - RICH_MENU_CLICK_WRAP_OVERHEAD
export const RICH_MENU_NAME_MAX_LENGTH = 300
export const RICH_MENU_CHAT_BAR_TEXT_MAX_LENGTH = 14

export type TRichMenuAreaAction = {
  type: ERichMenuAreaActionType
  label?: string
  uri?: string
  text?: string
  surveyId?: number
}

export type TCreateRichMenuRequest = {
  templateId: string
  name: string
  chatBarText: string
  selected: boolean
  areas: TRichMenuAreaAction[]
  imageUrl: string
  imageContentType: ERichMenuImageContentType
}

export function richMenuImageContentTypeFromMime(
  mime: string,
): ERichMenuImageContentType | null {
  switch (mime) {
    case ERichMenuImageContentType.Png:
      return ERichMenuImageContentType.Png
    case ERichMenuImageContentType.Jpg:
    case ERichMenuImageContentType.Jpeg:
      return ERichMenuImageContentType.Jpeg
    default:
      return null
  }
}

export type TRichMenuApplyAllItem = {
  lineChannelId: number
  richMenuId: string
}

export type TRichMenuApplyAllFailure = {
  lineChannelId: number
}

export type TRichMenuApplyAllDeferred = {
  lineChannelId: number
}

export type TRichMenuApplyAllResponse = {
  applied: TRichMenuApplyAllItem[]
  /** 未適用の全チャンネル。deferredのIDも含む。 */
  failed: TRichMenuApplyAllFailure[]
  /** 旧Backendでは未返却。deadlineで処理未開始だったfailedの内訳。 */
  deferred?: TRichMenuApplyAllDeferred[]
}

export type TRichMenuApplyAllResult = Omit<
  TRichMenuApplyAllResponse,
  'deferred'
> & {
  deferred: TRichMenuApplyAllDeferred[]
}

export type TRichMenuClearDefaultAllItem = {
  lineChannelId: number
}

export type TRichMenuClearDefaultAllFailure = {
  lineChannelId: number
}

export type TRichMenuClearDefaultAllResponse = {
  cleared: TRichMenuClearDefaultAllItem[]
  alreadyCleared: TRichMenuClearDefaultAllItem[]
  failed: TRichMenuClearDefaultAllFailure[]
}

export type TRichMenuOperationConflict = {
  statusCode: 409
  code: 'RICH_MENU_OPERATION_IN_PROGRESS'
  message: string
}

export type TLineRichMenuDetail = {
  richMenuId: string
  name: string
  chatBarText: string
  selected: boolean
  size: { width: number; height: number }
  areas: Array<{
    bounds: { x: number; y: number; width: number; height: number }
    action: {
      type: string
      label?: string
      uri?: string
      text?: string
      data?: string
    }
  }>
}

export type TRichMenuCurrentResponse = {
  lineChannelId: number
  richMenuId: string | null
  richMenu: TLineRichMenuDetail | null
  imageContentType: string | null
  imageBase64: string | null
}
